import { useFormType } from '../../../hooks/useFormType';
import { extractFormData, formatDate, formatFileSize } from '../../../utils/formTypeUtils';
import { modalRootClassName } from '../../../utils/modalRootClassName';
import { getContent, getTile } from '../../../utils/tileUtils';
import Breadcrumbs, { getCrumbs } from '../../common/Breadcrumbs';
import Messages from '../../includes/Messages';
import PromoBox from '../../includes/PromoBox';
import NotFound from '../NotFound';
import { connect } from 'react-redux';
import { Link, useLocation, useParams } from 'react-router';

/**
 * Shows a UI for interacting with a particular type of tile,
 * which in turn contains a group of form types, which have concrete instances of forms.
 */

const FormType = (props) => {
  const location = useLocation();
  const params = useParams();
  const { dashboard, tilesContent, formType, registrationFormType, config, sessionId } = props;

  // Use the custom hook for handlers
  const { fileDownloadHandler, filesClickHandler, deleteClickHandler, addClickHandler, addFormHandler, hideFilesModal, hideAddModal } =
    useFormType(sessionId);

  // urlParts and type logic may need to be adjusted if match.url is not available
  // If you need the matched base path, you can use match?.pattern?.path or similar depending on your router version
  const urlParts = location.pathname.replace(/^\/+/, '').replace(/\/+$/, '').split('/');
  const type = params.type;
  const tile = getTile(dashboard.tiles || [], type);

  if (!tile || !type) return <NotFound />;

  let { addTypes, forms, formsHasActions, completedForms, completedFormsHasFile, completedFormsHasPdf } = extractFormData(
    tile,
    registrationFormType,
  );

  let showAddButton = addTypes.length > 0 || Object.hasOwn(config.addRedirects ?? {}, type);

  const emptyContent = {
    intro: null,
    banner: {
      title: null,
      content: null,
    },
  };
  const content = getContent(tilesContent, type) || emptyContent;

  const passedBreadCrumbs = location.state?.breadCrumbs || [{ title: 'dashboard', url: '/' }];
  const currentBreadCrumb = {
    title: content.title || urlParts[urlParts.length - 1].replace(/_/g, ' '),
    url: location.pathname,
  };
  const breadCrumbs = getCrumbs(passedBreadCrumbs, currentBreadCrumb, 'FormType');

  return (
    <div className="form-type-list">
      {/* Files Modal */}
      {formType.showFilesPopup ? <div className="modal-backdrop fade show" aria-hidden="true" /> : null}
      <div
        className={modalRootClassName(formType.showFilesPopup)}
        tabIndex="-1"
        role="dialog"
        id="filesModal"
        aria-labelledby="filesModalLabel"
        aria-modal={formType.showFilesPopup}
        aria-hidden={!formType.showFilesPopup}
      >
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title" id="filesModalLabel">
                Files
              </h4>
            </div>
            <div className="modal-body">
              {formType.files.map((file) => (
                <div key={file.id}>
                  <button className="btn btn-link wrap-text" onClick={() => fileDownloadHandler(file)}>
                    <div>
                      <span className="nav-icon fi-stack fi-lg">
                        <i className={'sa fi-stack-1x ' + file.iconClass} />
                      </span>
                    </div>
                    {file.name} ({formatFileSize(file.fileSize)})
                  </button>
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <div className="Actions">
                <button className="btn btn-default" type="button" onClick={hideFilesModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {formType.showAddPopup ? <div className="modal-backdrop fade show" aria-hidden="true" /> : null}
      <div
        className={modalRootClassName(formType.showAddPopup)}
        tabIndex="-1"
        role="dialog"
        id="addModal"
        aria-labelledby="addModalLabel"
        aria-modal={formType.showAddPopup}
        aria-hidden={!formType.showAddPopup}
      >
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title" id="addModalLabel">
                Add form
              </h4>
            </div>
            <div className="modal-body">
              {/* eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml */}
              {content.add?.modalText && <div dangerouslySetInnerHTML={{ __html: content.add.modalText }} />}
              {addTypes.map((addType) => (
                <a
                  key={addType.id}
                  style={{ cursor: 'pointer' }}
                  className={'d-flex' + (formType.isCreating !== false ? ' disabled' : '')}
                  onClick={() => addFormHandler(addType.id, breadCrumbs)}
                >
                  <div className="nav-icon fi-stack fi-lg flex-shrink-0">
                    <i className={'sa fi-stack-1x ' + (formType.isCreating === addType.id ? 'fa-spin fa-spinner' : 'sa-invoice')} />
                  </div>
                  <div className="pt-0 pb-3 flex-grow">{addType.name}</div>
                </a>
              ))}
            </div>
            <div className="modal-footer">
              <div className="Actions">
                <button
                  className={'btn btn-default' + (formType.isCreating !== false ? ' disabled' : '')}
                  type="button"
                  onClick={hideAddModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Subtitle */}
      {content.subtitle && (
        <PromoBox>
          <p className="intro">{content.subtitle}</p>
        </PromoBox>
      )}

      {/* Breadcrumbs */}
      <Breadcrumbs crumbs={breadCrumbs} />

      {/* Messages and Actions */}
      <div className="container content">
        <div className="row middle">
          <div className="col-lg-12">
            <Messages />
          </div>
          <div className="col-lg-12">
            <div className="d-md-flex align-items-center justify-content-between">
              <h3>{content.title || tile.name}</h3>
              {showAddButton && (
                <button type="button" className="btn btn-primary" onClick={() => addClickHandler(addTypes, type, breadCrumbs, config)}>
                  <span className="fa fa-plus me-2" />
                  {content.add?.buttonText ? content.add.buttonText : 'Start new ' + tile.name}
                </button>
              )}
            </div>

            {/* In progress forms */}
            <section className="mt-4">
              <header>
                <h4>In progress</h4>
              </header>

              {forms.length === 0 && <div className="alert alert-info">No in progress forms.</div>}

              {forms.length > 0 && (
                <div className="responsiveTable responsiveTableHeader">
                  <div className="cell size-8">
                    <h5>Form: </h5>
                  </div>
                  <div className="cell size-8">
                    <h5>Date: </h5>
                  </div>
                  {formsHasActions && (
                    <div className="cell size-4">
                      <h5>Actions: </h5>
                    </div>
                  )}
                </div>
              )}

              {forms &&
                forms.length > 0 &&
                forms.map((form) => (
                  <div className="responsiveTable" key={form.formId}>
                    <div className="cell size-8">
                      <h5>Form: </h5>
                      {form.canEdit ? (
                        <Link to={{ pathname: '/form/' + form.formType + '/' + form.formId }} state={{ breadCrumbs: breadCrumbs }}>
                          {form.name}
                        </Link>
                      ) : (
                        <span>{form.name}</span>
                      )}
                    </div>
                    <div className="cell size-8">
                      <h5>Date: </h5>
                      <span className="date">{formatDate(form.writeDate)}</span>
                    </div>
                    {formsHasActions && (
                      <div className="cell size-4">
                        <h5>Actions: </h5>
                        {form.canDelete ? (
                          <button
                            className={'btn btn-default' + (formType.isDeleting ? ' disabled' : '')}
                            onClick={() => deleteClickHandler(form.formType, form.formId, form.name, form.writeDate)}
                          >
                            <span className="fas fa fa-times" /> Delete
                          </button>
                        ) : null}
                      </div>
                    )}
                  </div>
                ))}
            </section>

            {/* Completed forms */}
            <section>
              <header>
                <h4>Completed</h4>
              </header>

              {completedForms.length === 0 && <div className="alert alert-info">No completed forms.</div>}

              {completedForms.length > 0 && (
                <div className="responsiveTable responsiveTableHeader">
                  <div className="cell size-8">
                    <h5>Form: </h5>
                  </div>
                  <div className="cell size-8">
                    <h5>Date: </h5>
                  </div>
                  {completedFormsHasFile && (
                    <div className="cell size-2">
                      <h5>Files: </h5>
                    </div>
                  )}
                  {completedFormsHasPdf && (
                    <div className="cell size-2">
                      <h5>Pdf: </h5>
                    </div>
                  )}
                </div>
              )}

              {completedForms &&
                completedForms.map((form) => (
                  <div className="responsiveTable" key={form.formId}>
                    <div className="cell size-8">
                      <h5>Form: </h5>
                      <span>{form.name}</span>
                    </div>
                    <div className="cell size-8">
                      <h5>Date: </h5>
                      <span className="date">{formatDate(form.writeDate)}</span>
                    </div>
                    {completedFormsHasFile && (
                      <div className="cell size-2">
                        <h5>Files:</h5>
                        <button
                          className={'btn btn-link' + (form.files.length === 0 ? ' d-none' : '')}
                          onClick={() => filesClickHandler(form.files)}
                        >
                          <span className="nav-icon fi-stack fi-lg">
                            <i className="sa sa-invoice fi-stack-1x" />
                          </span>
                          <span className="visually-hidden">Files</span>
                        </button>
                      </div>
                    )}
                    {completedFormsHasPdf && (
                      <div className="cell size-2">
                        <h5>Pdf: </h5>
                        <button
                          className={'btn btn-link' + (form.renderedPdf === null ? ' d-none' : '')}
                          onClick={() => fileDownloadHandler(form.renderedPdf)}
                        >
                          <span className="nav-icon fi-stack fi-lg">
                            <i className="sa sa-pdf fi-stack-1x" />
                          </span>
                          <span className="visually-hidden">Pdf</span>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
            </section>
          </div>
        </div>
      </div>

      {/* Banner */}
      {content.banner.content && (
        <div className="page-title bg-black">
          <img alt={content.banner.title} src="/assets/Uploads/Photos/banner-about.jpg" className="banner hidden-sm hidden-xs" />
          <div className="container">
            <div className="row banner hidden-sm hidden-xs">
              <div className="col-lg-4 col-md-6 col-sm-6">
                <div className="img-overlay bg-black">
                  <div className="content-wrap ">
                    <span className="fi-stack fi-3x">
                      <i className="fi fi-circle-solid fi-stack-2x"></i>
                      <i className="fi fi-building fi-stack-1x fi-inverse"></i>
                    </span>

                    {content.banner.title ? <h2>{content.banner.title}</h2> : null}
                    {/* eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml */}
                    <div className="typography" dangerouslySetInnerHTML={{ __html: content.banner.content }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  dashboard: state.dashboard,
  formType: state.formType,
  config: state.config,
  tilesContent: state.config.content.tiles,
  registrationFormType: state.config.registrationFormType,
  sessionId: state.user.sessionId,
});

export default connect(mapStateToProps)(FormType);
